import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  FormHelperText,
  Input,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  Select,
  Spinner,
  Switch,
  Text,
  VStack,
  useToast,
} from "@chakra-ui/react";
import { supabasePublic, supabaseSchema } from "./supabaseClient";
import { parseComplianceSettings } from "../utils/complianceSettings";

const scrollStyles = {
  overflowY: "auto",
  WebkitOverflowScrolling: "touch",
  "&::-webkit-scrollbar": { width: "8px" },
  "&::-webkit-scrollbar-track": { background: "transparent" },
  "&::-webkit-scrollbar-thumb": {
    background: "var(--chakra-colors-gray-300)",
    borderRadius: "4px",
  },
};

function SwitchField({ label, isChecked, onChange, isDisabled }) {
  return (
    <FormControl>
      <Flex
        direction={{ base: "column", sm: "row" }}
        align={{ base: "flex-start", sm: "center" }}
        justify="space-between"
        gap={2}
        w="100%"
      >
        <FormLabel
          mb={0}
          flex="1"
          minW={0}
          fontSize={{ base: "sm", md: "md" }}
          lineHeight="short"
          pr={{ base: 0, sm: 2 }}
        >
          {label}
        </FormLabel>
        <Switch
          flexShrink={0}
          isChecked={isChecked}
          onChange={onChange}
          isDisabled={isDisabled}
          alignSelf={{ base: "flex-end", sm: "center" }}
        />
      </Flex>
    </FormControl>
  );
}

function activeUserSchemaName() {
  const raw = (supabaseSchema || "public").trim();
  return raw.includes(",") ? raw.split(",")[0].trim() : raw;
}

const ComplianceAdminSettings = ({ onClose, isOpen }) => {
  const toast = useToast();
  const [schemas, setSchemas] = useState([]);
  const [selectedSchema, setSelectedSchema] = useState("");
  const [settings, setSettings] = useState(parseComplianceSettings(null));
  const [loadingSchemas, setLoadingSchemas] = useState(true);
  const [loadingRow, setLoadingRow] = useState(false);
  const [saving, setSaving] = useState(false);

  const loadSchemaNames = useCallback(async () => {
    setLoadingSchemas(true);
    try {
      const { data, error } = await supabasePublic()
        .from("schema_settings")
        .select("schema_name")
        .order("schema_name");
      if (error) throw error;
      setSchemas((data || []).map((r) => r.schema_name).filter(Boolean));
    } catch (err) {
      toast({
        title: "Could not load schemas",
        description: err.message,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoadingSchemas(false);
    }
  }, [toast]);

  const loadRow = useCallback(async () => {
    if (!selectedSchema) return;
    setLoadingRow(true);
    try {
      const { data, error } = await supabasePublic()
        .from("schema_settings")
        .select("compliance_monitoring")
        .eq("schema_name", selectedSchema)
        .maybeSingle();
      if (error) throw error;
      setSettings(parseComplianceSettings(data?.compliance_monitoring));
    } catch (err) {
      toast({
        title: "Could not load compliance settings",
        description: err.message,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoadingRow(false);
    }
  }, [selectedSchema, toast]);

  useEffect(() => {
    loadSchemaNames();
  }, [loadSchemaNames]);

  useEffect(() => {
    if (!isOpen || schemas.length === 0) return;
    const preferred = activeUserSchemaName();
    setSelectedSchema(
      schemas.includes(preferred) ? preferred : schemas[0] || "",
    );
  }, [isOpen, schemas]);

  useEffect(() => {
    if (selectedSchema) loadRow();
  }, [selectedSchema, loadRow]);

  const setField = (key, value) =>
    setSettings((s) => ({ ...s, [key]: value }));

  const handleSave = async () => {
    if (!selectedSchema) return;
    setSaving(true);
    try {
      const { error } = await supabasePublic()
        .from("schema_settings")
        .update({ compliance_monitoring: settings })
        .eq("schema_name", selectedSchema);
      if (error) throw error;
      toast({
        title: "Saved",
        description: `Compliance settings updated for “${selectedSchema}”.`,
        status: "success",
        duration: 4000,
        isClosable: true,
      });
      if (onClose) onClose();
    } catch (err) {
      toast({
        title: "Save failed",
        description: err.message,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setSaving(false);
    }
  };

  if (loadingSchemas) {
    return (
      <Box py={6} textAlign="center">
        <Spinner />
        <Text mt={2} color="gray.500">
          Loading schemas…
        </Text>
      </Box>
    );
  }

  if (!schemas.length) {
    return (
      <Text color="gray.600" fontSize={{ base: "sm", md: "md" }}>
        No rows in <code>schema_settings</code>. Run the compliance SQL
        migration first.
      </Text>
    );
  }

  return (
    <VStack spacing={0} align="stretch" w="100%" minH={0}>
      <Box flexShrink={0} pb={3}>
        <FormControl>
          <FormLabel fontSize={{ base: "sm", md: "md" }}>Schema</FormLabel>
          <Select
            value={selectedSchema}
            onChange={(e) => setSelectedSchema(e.target.value)}
            isDisabled={loadingRow}
            size={{ base: "sm", md: "md" }}
          >
            {schemas.map((name) => (
              <option key={name} value={name}>
                {name}
              </option>
            ))}
          </Select>
        </FormControl>
      </Box>

      <Box
        flex="1"
        minH={0}
        maxH={{ base: "calc(90vh - 220px)", sm: "calc(85vh - 200px)", md: "65vh" }}
        overflowY="auto"
        pr={{ base: 1, md: 2 }}
        sx={scrollStyles}
      >
        {loadingRow ? (
          <Box textAlign="center" py={8}>
            <Spinner size="sm" />
          </Box>
        ) : (
          <VStack spacing={4} align="stretch" pb={2}>
            <SwitchField
              label="Compliance monitoring enabled"
              isChecked={settings.enabled}
              onChange={(e) => setField("enabled", e.target.checked)}
            />
            <SwitchField
              label="Medication non-compliance monitor"
              isChecked={settings.medication_monitor_enabled}
              onChange={(e) =>
                setField("medication_monitor_enabled", e.target.checked)
              }
              isDisabled={!settings.enabled}
            />
            <SwitchField
              label="Preventive care gap alerts"
              isChecked={settings.preventive_gaps_enabled}
              onChange={(e) =>
                setField("preventive_gaps_enabled", e.target.checked)
              }
              isDisabled={!settings.enabled}
            />
            <FormControl>
              <FormLabel fontSize={{ base: "sm", md: "md" }}>
                Critical med refill grace (days)
              </FormLabel>
              <NumberInput
                min={1}
                max={90}
                size={{ base: "sm", md: "md" }}
                value={settings.medication_refill_grace_days}
                onChange={(_, v) =>
                  setField("medication_refill_grace_days", v || 14)
                }
                isDisabled={!settings.enabled}
              >
                <NumberInputField />
                <NumberInputStepper>
                  <NumberIncrementStepper />
                  <NumberDecrementStepper />
                </NumberInputStepper>
              </NumberInput>
              <FormHelperText fontSize="xs">
                Alert when critical meds unfilled past this window (default 14).
              </FormHelperText>
            </FormControl>
            <FormControl>
              <FormLabel fontSize={{ base: "sm", md: "md" }}>
                Preventive warning lead time (days)
              </FormLabel>
              <NumberInput
                min={7}
                max={180}
                size={{ base: "sm", md: "md" }}
                value={settings.preventive_warning_days}
                onChange={(_, v) =>
                  setField("preventive_warning_days", v || 30)
                }
                isDisabled={!settings.enabled}
              >
                <NumberInputField />
                <NumberInputStepper>
                  <NumberIncrementStepper />
                  <NumberDecrementStepper />
                </NumberInputStepper>
              </NumberInput>
              <FormHelperText fontSize="xs">
                Days before a gap becomes a quality miss (default 30).
              </FormHelperText>
            </FormControl>
            <SwitchField
              label="Auto-document chart on outreach"
              isChecked={settings.auto_document_chart}
              onChange={(e) =>
                setField("auto_document_chart", e.target.checked)
              }
            />

            <Text fontSize="sm" fontWeight="semibold" color="gray.600" pt={1}>
              Integrations
            </Text>
            <SwitchField
              label="Scheduled gap scan (background worker)"
              isChecked={settings.scheduled_evaluate_enabled}
              onChange={(e) =>
                setField("scheduled_evaluate_enabled", e.target.checked)
              }
              isDisabled={!settings.enabled}
            />
            <FormControl>
              <FormLabel fontSize={{ base: "sm", md: "md" }}>
                Scan interval (hours)
              </FormLabel>
              <NumberInput
                min={1}
                max={168}
                size={{ base: "sm", md: "md" }}
                value={settings.scheduled_evaluate_hours}
                onChange={(_, v) =>
                  setField("scheduled_evaluate_hours", v || 24)
                }
                isDisabled={!settings.enabled}
              >
                <NumberInputField />
                <NumberInputStepper>
                  <NumberIncrementStepper />
                  <NumberDecrementStepper />
                </NumberInputStepper>
              </NumberInput>
            </FormControl>
            <SwitchField
              label="Patient SMS / app notifications"
              isChecked={settings.patient_notifications_enabled}
              onChange={(e) =>
                setField("patient_notifications_enabled", e.target.checked)
              }
              isDisabled={!settings.enabled}
            />
            <SwitchField
              label="Auto-notify on new gaps"
              isChecked={settings.auto_notify_on_new_alert}
              onChange={(e) =>
                setField("auto_notify_on_new_alert", e.target.checked)
              }
              isDisabled={
                !settings.enabled || !settings.patient_notifications_enabled
              }
            />
            <SwitchField
              label="Epic care gaps import"
              isChecked={settings.epic_care_gaps_enabled}
              onChange={(e) =>
                setField("epic_care_gaps_enabled", e.target.checked)
              }
              isDisabled={!settings.enabled}
            />
            <FormControl>
              <FormLabel fontSize={{ base: "sm", md: "md" }}>
                Patient scheduling link base URL
              </FormLabel>
              <Input
                size={{ base: "sm", md: "md" }}
                value={settings.scheduling_base_url}
                onChange={(e) =>
                  setField("scheduling_base_url", e.target.value)
                }
                placeholder="https://app.vitamedai.com/schedules"
                isDisabled={!settings.enabled}
              />
              <FormHelperText fontSize="xs">
                Used in patient SMS and app deep links.
              </FormHelperText>
            </FormControl>
            <FormControl>
              <FormLabel fontSize={{ base: "sm", md: "md" }}>
                Surescripts webhook secret
              </FormLabel>
              <Input
                size={{ base: "sm", md: "md" }}
                type="password"
                value={settings.surescripts_webhook_secret}
                onChange={(e) =>
                  setField("surescripts_webhook_secret", e.target.value)
                }
                placeholder="X-Compliance-Webhook-Secret"
                isDisabled={!settings.enabled}
              />
              <FormHelperText fontSize="xs" wordBreak="break-word">
                POST /api/compliance/webhooks/surescripts/
              </FormHelperText>
            </FormControl>
          </VStack>
        )}
      </Box>

      <Box
        flexShrink={0}
        pt={4}
        mt={2}
        borderTopWidth="1px"
        borderColor="gray.200"
        bg="white"
        position="sticky"
        bottom={0}
      >
        <Button
          colorScheme="blue"
          w="100%"
          size={{ base: "md", md: "md" }}
          onClick={handleSave}
          isLoading={saving}
          isDisabled={!selectedSchema || loadingRow}
        >
          Save
        </Button>
      </Box>
    </VStack>
  );
};

export default ComplianceAdminSettings;
